import React, { useCallback } from "react";
import { Button, Input, InputNumber, Select, Switch, Collapse, Alert, Tag } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPlus } from "@fortawesome/free-solid-svg-icons";
import { AcceptRa } from "../../../flatbuffers/generated/net-config/accept-ra";

// ************************************************ */
// EDITOR TYPES *********************************** */
// These mirror NetConfig.SiteNetwork field for field. That schema in turn
// mirrors the NIC_n_*/BOND_n_*/VLAN_n_*/ROUTE_n_*/RULE_n_* records of a
// pxe_provision gold-image profile, so a network configured here is applied by
// the same netplan generator — and checked by the same validator — as one
// baked into an image. Renaming a field here without changing the schema
// silently stops it reaching the edge server.

export interface NicRow {
  id: string;
  mac: string;
  /** Kernel name or glob, matched when no MAC is known. */
  name: string;
  /** Rename the matched device to this. */
  setName: string;
  /** Stops boot waiting on a port that is not plugged in. */
  optional: boolean;
  role: string;
  dhcp4: boolean;
  dhcp6: boolean;
  acceptRa: AcceptRa;
  addresses: string;
  gateway4: string;
  gateway6: string;
  mtu: number | null;
}

export interface BondRow {
  id: string;
  members: string;
  mode: string;
  lacpRate: string;
  hashPolicy: string;
  /** Link monitoring, in milliseconds. 0 disables it. */
  miiInterval: number | null;
  upDelay: number | null;
  downDelay: number | null;
  minLinks: number | null;
  /** NIC id preferred while it is up. Only meaningful for the failover modes. */
  primary: string;
  dhcp4: boolean;
  dhcp6: boolean;
  acceptRa: AcceptRa;
  addresses: string;
  gateway4: string;
  gateway6: string;
  mtu: number | null;
}

export interface VlanRow {
  id: string;
  tag: number | null;
  link: string;
  dhcp4: boolean;
  dhcp6: boolean;
  acceptRa: AcceptRa;
  addresses: string;
  gateway4: string;
  gateway6: string;
  mtu: number | null;
}

export interface RouteRow {
  on: string;
  to: string;
  via: string;
  tableId: number | null;
  metric: number | null;
  onLink: boolean;
}

export interface RuleRow {
  on: string;
  from: string;
  to: string;
  tableId: number | null;
  priority: number | null;
  /** Firewall mark to match on. */
  mark: number | null;
}

export interface SiteNetworkForm {
  nics: NicRow[];
  bonds: BondRow[];
  vlans: VlanRow[];
  routes: RouteRow[];
  rules: RuleRow[];
  probeTarget: string;
  allowedCidrs: string;
  nameservers: string;
  searchDomains: string;
}

export const emptySiteNetwork = (): SiteNetworkForm => ({
  nics: [],
  bonds: [],
  vlans: [],
  routes: [],
  rules: [],
  probeTarget: "",
  allowedCidrs: "",
  nameservers: "",
  searchDomains: "",
});

const newNic = (): NicRow => ({
  id: "",
  mac: "",
  name: "",
  setName: "",
  optional: false,
  role: "standalone",
  dhcp4: true,
  dhcp6: false,
  acceptRa: AcceptRa.UNSET,
  addresses: "",
  gateway4: "",
  gateway6: "",
  mtu: null,
});

const newBond = (): BondRow => ({
  id: "",
  members: "",
  mode: "802.3ad",
  lacpRate: "fast",
  hashPolicy: "layer3+4",
  miiInterval: null,
  upDelay: null,
  downDelay: null,
  minLinks: null,
  primary: "",
  dhcp4: false,
  dhcp6: false,
  acceptRa: AcceptRa.UNSET,
  addresses: "",
  gateway4: "",
  gateway6: "",
  mtu: null,
});

const newVlan = (): VlanRow => ({
  id: "",
  tag: null,
  link: "",
  dhcp4: false,
  dhcp6: false,
  acceptRa: AcceptRa.UNSET,
  addresses: "",
  gateway4: "",
  gateway6: "",
  mtu: null,
});

const newRoute = (): RouteRow => ({
  on: "",
  to: "default",
  via: "",
  tableId: null,
  metric: null,
  onLink: false,
});

const newRule = (): RuleRow => ({
  on: "",
  from: "",
  to: "",
  tableId: null,
  priority: null,
  mark: null,
});

// Bond modes netplan accepts. Kept as a list rather than free text because a
// typo here comes up as a degraded bond running at one link's throughput,
// which looks like a working machine.
const ACCEPT_RA_OPTIONS = [
  { value: AcceptRa.UNSET, label: "default" },
  { value: AcceptRa.ENABLED, label: "accept" },
  { value: AcceptRa.DISABLED, label: "refuse" },
];

const LACP_MODES = ["802.3ad"];
const HASH_POLICY_MODES = ["802.3ad", "balance-xor"];
/** Modes with a notion of an active member; matches netplan.sh's own set. */
const PRIMARY_MODES = ["active-backup", "balance-tlb", "balance-alb"];
const LACP_RATES = ["fast", "slow"];
const HASH_POLICIES = ["layer2", "layer2+3", "layer3+4", "encap2+3", "encap3+4"];

const BOND_MODES = [
  "802.3ad",
  "active-backup",
  "balance-rr",
  "balance-xor",
  "broadcast",
  "balance-tlb",
  "balance-alb",
];

// ************************************************ */
// VALIDATION ************************************* */
// The same rules the build-time validator applies, run here so an operator
// sees the problem while typing rather than after a box fails to come up.

const isV6 = (value: string) => value.includes(":");

/**
 * Route and rule problems that are silently inert rather than loud.
 *
 * A route to an IPv6 destination via an IPv4 gateway is YAML netplan accepts
 * and networkd rejects at apply time — on a headless box whose network has
 * already been torn down to apply it. A rule matching a v4 source against a v6
 * destination matches nothing at all, so the machine works right up until
 * traffic takes the path the rule existed to fix.
 */
/**
 * Whether the operator has put anything into the site network at all.
 *
 * This answers "is there something here worth persisting?", deliberately NOT
 * "is it valid". The draft hook skips its PUT when validate() returns false, so
 * a half-typed network that still has problems is exactly the state a draft
 * exists to protect — judging it by siteNetworkProblems would throw away the
 * work the operator is in the middle of.
 *
 * Mirrors emptySiteNetwork(): every field it initialises is checked here, so a
 * field added there without a matching clause here would silently stop counting
 * as content.
 */
export const siteNetworkHasContent = (network?: SiteNetworkForm): boolean => {
  if (!network) return false;

  const rows = [network.nics, network.bonds, network.vlans, network.routes, network.rules];
  if (rows.some((row) => Array.isArray(row) && row.length > 0)) return true;

  const text = [
    network.probeTarget,
    network.allowedCidrs,
    network.nameservers,
    network.searchDomains,
  ];

  return text.some((value) => typeof value === "string" && value.trim() !== "");
};

export const siteNetworkProblems = (network: SiteNetworkForm): string[] => {
  const problems: string[] = [];

  // The schema marks these fields `required`, which is a wire-level constraint,
  // not a hint: the generated pack() throws when one is empty. Caught here they
  // are a red line under a field; caught there they are an exception thrown from
  // inside the FlatBuffer builder, after the operator hits Save, naming a schema
  // field they have never heard of and losing everything else they typed.
  network.nics.forEach((nic, index) => {
    if (!nic.id.trim()) problems.push(`Interface ${index + 1}: needs an id.`);
    // The renderer refuses a netdef with nothing to match a device by, and it
    // is right to: an empty match block does not mean "any interface", it
    // means the netdef never binds and the interface is never configured.
    if (!nic.mac.trim() && !nic.name.trim()) {
      problems.push(`Interface ${index + 1}: needs a MAC or a name to match the device by.`);
    }
  });
  network.bonds.forEach((bond, index) => {
    if (!bond.id.trim()) problems.push(`Bond ${index + 1}: needs an id.`);
  });
  network.vlans.forEach((vlan, index) => {
    if (!vlan.id.trim()) problems.push(`VLAN ${index + 1}: needs an id.`);
    if (!vlan.link.trim()) {
      problems.push(`VLAN ${index + 1}: needs a link — the NIC or bond it rides on.`);
    }
  });
  network.routes.forEach((route, index) => {
    if (!route.on.trim()) problems.push(`Route ${index + 1}: needs the interface it applies to.`);
    if (!route.to.trim()) problems.push(`Route ${index + 1}: needs a destination.`);
  });
  network.rules.forEach((rule, index) => {
    if (!rule.on.trim()) problems.push(`Rule ${index + 1}: needs the interface it applies to.`);
  });

  network.routes.forEach((route, index) => {
    if (!route.via || route.to === "default" || !route.to) return;
    if (isV6(route.to) !== isV6(route.via)) {
      problems.push(
        `Route ${index + 1}: "${route.to}" and gateway "${route.via}" are different address families — a route cannot cross families, and networkd rejects it when the box applies it.`,
      );
    }
  });

  network.rules.forEach((rule, index) => {
    if (!rule.from || !rule.to) return;
    if (isV6(rule.from) !== isV6(rule.to)) {
      problems.push(
        `Rule ${index + 1}: source "${rule.from}" and destination "${rule.to}" are different address families — the rule would never match anything.`,
      );
    }
  });

  const declaredIds = new Set<string>([
    ...network.nics.map((n) => n.id),
    ...network.bonds.map((b) => b.id),
    ...network.vlans.map((v) => v.id),
  ]);

  network.bonds.forEach((bond, index) => {
    bond.members
      .split(",")
      .map((m) => m.trim())
      .filter(Boolean)
      .forEach((member) => {
        const nic = network.nics.find((candidate) => candidate.id === member);
        if (!nic) {
          problems.push(
            `Bond ${index + 1}: member "${member}" is not one of the NICs declared above.`,
          );
        } else if (nic.role !== "bond-member") {
          problems.push(
            `Bond ${index + 1}: "${member}" is enslaved here but its role is still "${nic.role}".`,
          );
        }
      });
  });

  // A rule with no table selects nothing, and 0 is the one value the renderer
  // rejects outright — the schema comment says in as many words to validate it
  // where the rule is authored, which is here.
  network.rules.forEach((rule, index) => {
    if (rule.tableId === null || rule.tableId === 0) {
      problems.push(
        `Rule ${index + 1}: needs a routing table — a rule with no table matches nothing.`,
      );
    }
    if (!rule.from.trim() && !rule.to.trim()) {
      problems.push(
        `Rule ${index + 1}: needs a source or a destination, otherwise it never matches.`,
      );
    }
  });

  // A VLAN tag is 12-bit. Out of range it wraps on the wire to a valid tag on
  // a different network, which is worse than being rejected.
  network.vlans.forEach((vlan, index) => {
    if (vlan.tag === null || vlan.tag < 1 || vlan.tag > 4094) {
      problems.push(`VLAN ${index + 1}: tag must be between 1 and 4094.`);
    }
  });

  // A default route with no gateway is what an untouched new row looks like.
  network.routes.forEach((route, index) => {
    if (route.to.trim() === "default" && !route.via.trim()) {
      problems.push(`Route ${index + 1}: a default route needs a gateway.`);
    }
  });

  // Two netdefs with one id is not a thing netplan can express, and a Set
  // hides the collision from every other check in this function.
  const seen = new Set<string>();
  [...network.nics, ...network.bonds, ...network.vlans].forEach((row) => {
    const id = row.id.trim();
    if (!id) return;
    if (seen.has(id)) problems.push(`"${id}" is used as an id more than once.`);
    seen.add(id);
  });

  network.vlans.forEach((vlan, index) => {
    if (vlan.link && !declaredIds.has(vlan.link)) {
      problems.push(`VLAN ${index + 1}: link "${vlan.link}" is not a declared NIC or bond.`);
    }
  });

  network.routes.forEach((route, index) => {
    if (route.on && !declaredIds.has(route.on)) {
      problems.push(`Route ${index + 1}: "${route.on}" is not a declared NIC, bond or VLAN.`);
    }
  });
  network.rules.forEach((rule, index) => {
    if (rule.on && !declaredIds.has(rule.on)) {
      problems.push(`Rule ${index + 1}: "${rule.on}" is not a declared NIC, bond or VLAN.`);
    }
  });

  // A network with interfaces but no probe target cannot be rolled back: the
  // box applies it, cannot reach anything, and has no way to tell.
  if (declaredIds.size > 0 && !network.probeTarget.trim()) {
    problems.push(
      "No reachability probe target. Without one a bad network cannot be rolled back, and the box is stranded with it.",
    );
  }

  return problems;
};

/**
 * The keys whose values are editable row lists.
 *
 * Narrower than `keyof SiteNetworkForm`, and deliberately so: the row helpers
 * index into an array, and `probeTarget`/`allowedCidrs` are plain strings.
 * Typing them as any key made the compiler ask for a cast at every call, which
 * is the compiler correctly pointing out that `updateRow("probeTarget", 0, …)`
 * is nonsense.
 */
type RowKey = "nics" | "bonds" | "vlans" | "routes" | "rules";

interface Props {
  value: SiteNetworkForm;
  onChange: (next: SiteNetworkForm) => void;
  disabled?: boolean;
}

function ServerNetworkEditor({ value, onChange, disabled = false }: Props) {
  const patch = useCallback(
    <K extends keyof SiteNetworkForm>(key: K, next: SiteNetworkForm[K]) => {
      onChange({ ...value, [key]: next });
    },
    [onChange, value],
  );

  const updateRow = useCallback(
    (key: RowKey, index: number, field: string, fieldValue: unknown) => {
      const rows = [...(value[key] as unknown as Record<string, unknown>[])];
      rows[index] = { ...rows[index], [field]: fieldValue };
      patch(key, rows as unknown as SiteNetworkForm[RowKey]);
    },
    [patch, value],
  );

  const removeRow = useCallback(
    (key: RowKey, index: number) => {
      const rows = [...(value[key] as unknown[])];
      rows.splice(index, 1);
      patch(key, rows as unknown as SiteNetworkForm[RowKey]);
    },
    [patch, value],
  );

  const addRow = useCallback(
    (key: RowKey, row: unknown) => {
      patch(key, [...(value[key] as unknown[]), row] as unknown as SiteNetworkForm[RowKey]);
    },
    [patch, value],
  );

  const problems = siteNetworkProblems(value);

  const rowStyle: React.CSSProperties = {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    marginBottom: "8px",
    flexWrap: "wrap",
  };

  // antd 4's Collapse takes Panel children, not an `items` prop. The sections
  // stay as data so each is declared in one place, and the Panels map from them.
  const sections: { key: string; label: React.ReactNode; children: React.ReactNode }[] = [
    {
      key: "nics",
      label: (
        <span>
          Interfaces <Tag>{value.nics.length}</Tag>
        </span>
      ),
      children: (
        <div>
          {value.nics.map((nic, index) => (
            <div key={`nic-${index}`} style={rowStyle}>
              <Input
                style={{ width: "130px" }}
                placeholder="id (mgmt)"
                value={nic.id}
                disabled={disabled}
                onChange={(e) => updateRow("nics", index, "id", e.target.value)}
              />
              <Input
                style={{ width: "180px" }}
                placeholder="MAC to match on"
                value={nic.mac}
                disabled={disabled}
                onChange={(e) => updateRow("nics", index, "mac", e.target.value)}
              />
              <Input
                style={{ width: "130px" }}
                placeholder="name / glob"
                aria-label={`Kernel name or glob for interface ${index + 1}`}
                value={nic.name}
                disabled={disabled}
                onChange={(e) => updateRow("nics", index, "name", e.target.value)}
              />
              <Input
                style={{ width: "130px" }}
                placeholder="set-name"
                aria-label={`Rename interface ${index + 1} to`}
                value={nic.setName}
                disabled={disabled}
                onChange={(e) => updateRow("nics", index, "setName", e.target.value)}
              />
              <Select
                style={{ width: "150px" }}
                value={nic.role}
                disabled={disabled}
                onChange={(v) => updateRow("nics", index, "role", v)}
                options={[
                  { value: "standalone", label: "standalone" },
                  { value: "bond-member", label: "bond member" },
                ]}
              />
              <Input
                style={{ width: "260px" }}
                placeholder="addresses (10.0.0.5/24, 2001:db8::5/64)"
                value={nic.addresses}
                disabled={disabled}
                onChange={(e) => updateRow("nics", index, "addresses", e.target.value)}
              />
              <Input
                style={{ width: "150px" }}
                placeholder="gateway4"
                value={nic.gateway4}
                disabled={disabled}
                onChange={(e) => updateRow("nics", index, "gateway4", e.target.value)}
              />
              <Input
                style={{ width: "170px" }}
                placeholder="gateway6"
                value={nic.gateway6}
                disabled={disabled}
                onChange={(e) => updateRow("nics", index, "gateway6", e.target.value)}
              />
              <InputNumber
                style={{ width: "100px" }}
                placeholder="MTU"
                value={nic.mtu}
                min={68}
                max={65535}
                precision={0}
                aria-label={`MTU for interface ${index + 1}`}
                disabled={disabled}
                onChange={(v) => updateRow("nics", index, "mtu", v)}
              />
              <span title="Stops boot waiting on this port. A port that is not plugged in otherwise holds systemd-networkd-wait-online for its full timeout, every boot.">
                Optional{" "}
                <Switch
                  size="small"
                  checked={nic.optional}
                  disabled={disabled}
                  onChange={(c) => updateRow("nics", index, "optional", c)}
                />
              </span>
              <span>
                DHCPv4{" "}
                <Switch
                  size="small"
                  checked={nic.dhcp4}
                  disabled={disabled}
                  onChange={(c) => updateRow("nics", index, "dhcp4", c)}
                />
              </span>
              <span>
                DHCPv6{" "}
                <Switch
                  size="small"
                  checked={nic.dhcp6}
                  disabled={disabled}
                  onChange={(c) => updateRow("nics", index, "dhcp6", c)}
                />
              </span>
              <span title="Router advertisements. Separate from DHCPv6: RAs alone give an address and a default route (SLAAC); DHCPv6 gives an address from a server. Leave it unset to keep the kernel default; a switch could only say yes or no, and 'unset' is a third, different instruction.">
                Accept RA{" "}
                <Select
                  size="small"
                  style={{ width: "110px" }}
                  aria-label={`Accept router advertisements for interface ${index + 1}`}
                  value={nic.acceptRa}
                  disabled={disabled}
                  onChange={(v) => updateRow("nics", index, "acceptRa", v)}
                  options={ACCEPT_RA_OPTIONS}
                />
              </span>
              <Button
                danger
                size="small"
                disabled={disabled}
                icon={<FontAwesomeIcon icon={faTrash} />}
                aria-label={`Remove interface ${index + 1}`}
                onClick={() => removeRow("nics", index)}
              />
            </div>
          ))}
          <Button
            size="small"
            disabled={disabled}
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => addRow("nics", newNic())}
          >
            Add interface
          </Button>
        </div>
      ),
    },
    {
      key: "bonds",
      label: (
        <span>
          Teaming / bonds <Tag>{value.bonds.length}</Tag>
        </span>
      ),
      children: (
        <div>
          {value.bonds.map((bond, index) => (
            <div key={`bond-${index}`} style={rowStyle}>
              <Input
                style={{ width: "130px" }}
                placeholder="id (bond0)"
                value={bond.id}
                disabled={disabled}
                onChange={(e) => updateRow("bonds", index, "id", e.target.value)}
              />
              <Input
                style={{ width: "220px" }}
                placeholder="members (field-a, field-b)"
                value={bond.members}
                disabled={disabled}
                onChange={(e) => updateRow("bonds", index, "members", e.target.value)}
              />
              <Select
                style={{ width: "160px" }}
                aria-label={`Mode for bond ${index + 1}`}
                value={bond.mode}
                disabled={disabled}
                onChange={(v) => updateRow("bonds", index, "mode", v)}
                options={BOND_MODES.map((m) => ({ value: m, label: m }))}
              />
              {/* Shown per mode rather than always. A transmit-hash policy on
                  active-backup does nothing, and a control that does nothing
                  is one an operator sets and then wonders about. */}
              {LACP_MODES.includes(bond.mode) && (
                <Select
                  style={{ width: "120px" }}
                  aria-label={`LACP rate for bond ${index + 1}`}
                  value={bond.lacpRate || undefined}
                  placeholder="lacp rate"
                  allowClear
                  disabled={disabled}
                  onChange={(v) => updateRow("bonds", index, "lacpRate", v ?? "")}
                  options={LACP_RATES.map((r) => ({ value: r, label: r }))}
                />
              )}
              {HASH_POLICY_MODES.includes(bond.mode) && (
                <Select
                  style={{ width: "140px" }}
                  aria-label={`Transmit hash policy for bond ${index + 1}`}
                  value={bond.hashPolicy || undefined}
                  placeholder="hash policy"
                  allowClear
                  disabled={disabled}
                  onChange={(v) => updateRow("bonds", index, "hashPolicy", v ?? "")}
                  options={HASH_POLICIES.map((h) => ({ value: h, label: h }))}
                />
              )}
              {PRIMARY_MODES.includes(bond.mode) && (
                <Input
                  style={{ width: "150px" }}
                  placeholder="primary (NIC id)"
                  aria-label={`Primary member for bond ${index + 1}`}
                  value={bond.primary}
                  disabled={disabled}
                  onChange={(e) => updateRow("bonds", index, "primary", e.target.value)}
                />
              )}
              <Input
                style={{ width: "260px" }}
                placeholder="addresses"
                aria-label={`Addresses for bond ${index + 1}`}
                value={bond.addresses}
                disabled={disabled}
                onChange={(e) => updateRow("bonds", index, "addresses", e.target.value)}
              />
              <Input
                style={{ width: "150px" }}
                placeholder="gateway4"
                aria-label={`IPv4 gateway for bond ${index + 1}`}
                value={bond.gateway4}
                disabled={disabled}
                onChange={(e) => updateRow("bonds", index, "gateway4", e.target.value)}
              />
              <Input
                style={{ width: "170px" }}
                placeholder="gateway6"
                aria-label={`IPv6 gateway for bond ${index + 1}`}
                value={bond.gateway6}
                disabled={disabled}
                onChange={(e) => updateRow("bonds", index, "gateway6", e.target.value)}
              />
              <InputNumber
                style={{ width: "100px" }}
                placeholder="MTU"
                min={68}
                max={65535}
                precision={0}
                aria-label={`MTU for bond ${index + 1}`}
                value={bond.mtu}
                disabled={disabled}
                onChange={(v) => updateRow("bonds", index, "mtu", v)}
              />
              {/* Link monitoring. Without it a member whose link drops is never
                  removed and traffic keeps hashing onto a dead port. */}
              <InputNumber
                style={{ width: "110px" }}
                placeholder="mii (ms)"
                min={0}
                max={4294967295}
                precision={0}
                aria-label={`Link monitor interval for bond ${index + 1}`}
                value={bond.miiInterval}
                disabled={disabled}
                onChange={(v) => updateRow("bonds", index, "miiInterval", v)}
              />
              <InputNumber
                style={{ width: "110px" }}
                placeholder="up delay"
                min={0}
                max={4294967295}
                precision={0}
                aria-label={`Up delay for bond ${index + 1}`}
                value={bond.upDelay}
                disabled={disabled}
                onChange={(v) => updateRow("bonds", index, "upDelay", v)}
              />
              <InputNumber
                style={{ width: "110px" }}
                placeholder="down delay"
                min={0}
                max={4294967295}
                precision={0}
                aria-label={`Down delay for bond ${index + 1}`}
                value={bond.downDelay}
                disabled={disabled}
                onChange={(v) => updateRow("bonds", index, "downDelay", v)}
              />
              {LACP_MODES.includes(bond.mode) && (
                <InputNumber
                  style={{ width: "110px" }}
                  placeholder="min links"
                  min={0}
                  max={4294967295}
                  precision={0}
                  aria-label={`Minimum links for bond ${index + 1}`}
                  value={bond.minLinks}
                  disabled={disabled}
                  onChange={(v) => updateRow("bonds", index, "minLinks", v)}
                />
              )}
              <span>
                DHCPv4{" "}
                <Switch
                  size="small"
                  checked={bond.dhcp4}
                  disabled={disabled}
                  onChange={(c) => updateRow("bonds", index, "dhcp4", c)}
                />
              </span>
              <span>
                DHCPv6{" "}
                <Switch
                  size="small"
                  checked={bond.dhcp6}
                  disabled={disabled}
                  onChange={(c) => updateRow("bonds", index, "dhcp6", c)}
                />
              </span>
              <span>
                Accept RA{" "}
                <Select
                  size="small"
                  style={{ width: "110px" }}
                  aria-label={`Accept router advertisements for bond ${index + 1}`}
                  value={bond.acceptRa}
                  disabled={disabled}
                  onChange={(v) => updateRow("bonds", index, "acceptRa", v)}
                  options={ACCEPT_RA_OPTIONS}
                />
              </span>
              <Button
                danger
                size="small"
                disabled={disabled}
                icon={<FontAwesomeIcon icon={faTrash} />}
                aria-label={`Remove bond ${index + 1}`}
                onClick={() => removeRow("bonds", index)}
              />
            </div>
          ))}
          <Button
            size="small"
            disabled={disabled}
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => addRow("bonds", newBond())}
          >
            Add bond
          </Button>
          <div style={{ marginTop: "8px", opacity: 0.7, fontSize: "12px" }}>
            802.3ad needs the upstream switch ports in a matching LACP port-channel. Without that
            the bond comes up degraded — one active link, not two — which looks like a working
            machine.
          </div>
        </div>
      ),
    },
    {
      key: "vlans",
      label: (
        <span>
          VLANs <Tag>{value.vlans.length}</Tag>
        </span>
      ),
      children: (
        <div>
          {value.vlans.map((vlan, index) => (
            <div key={`vlan-${index}`} style={rowStyle}>
              <Input
                style={{ width: "130px" }}
                placeholder="id (bacnet)"
                value={vlan.id}
                disabled={disabled}
                onChange={(e) => updateRow("vlans", index, "id", e.target.value)}
              />
              <InputNumber
                style={{ width: "100px" }}
                placeholder="tag"
                value={vlan.tag}
                min={1}
                max={4094}
                precision={0}
                aria-label={`VLAN tag for VLAN ${index + 1}`}
                disabled={disabled}
                onChange={(v) => updateRow("vlans", index, "tag", v)}
              />
              <Input
                style={{ width: "150px" }}
                placeholder="link (bond0)"
                value={vlan.link}
                disabled={disabled}
                onChange={(e) => updateRow("vlans", index, "link", e.target.value)}
              />
              <Input
                style={{ width: "260px" }}
                placeholder="addresses"
                value={vlan.addresses}
                disabled={disabled}
                onChange={(e) => updateRow("vlans", index, "addresses", e.target.value)}
              />
              <Input
                style={{ width: "150px" }}
                placeholder="gateway4"
                aria-label={`IPv4 gateway for VLAN ${index + 1}`}
                value={vlan.gateway4}
                disabled={disabled}
                onChange={(e) => updateRow("vlans", index, "gateway4", e.target.value)}
              />
              <Input
                style={{ width: "170px" }}
                placeholder="gateway6"
                aria-label={`IPv6 gateway for VLAN ${index + 1}`}
                value={vlan.gateway6}
                disabled={disabled}
                onChange={(e) => updateRow("vlans", index, "gateway6", e.target.value)}
              />
              <InputNumber
                style={{ width: "100px" }}
                placeholder="MTU"
                min={68}
                max={65535}
                precision={0}
                aria-label={`MTU for VLAN ${index + 1}`}
                value={vlan.mtu}
                disabled={disabled}
                onChange={(v) => updateRow("vlans", index, "mtu", v)}
              />
              <span>
                DHCPv4{" "}
                <Switch
                  size="small"
                  checked={vlan.dhcp4}
                  disabled={disabled}
                  onChange={(c) => updateRow("vlans", index, "dhcp4", c)}
                />
              </span>
              <span>
                DHCPv6{" "}
                <Switch
                  size="small"
                  checked={vlan.dhcp6}
                  disabled={disabled}
                  onChange={(c) => updateRow("vlans", index, "dhcp6", c)}
                />
              </span>
              <span>
                Accept RA{" "}
                <Select
                  size="small"
                  style={{ width: "110px" }}
                  aria-label={`Accept router advertisements for VLAN ${index + 1}`}
                  value={vlan.acceptRa}
                  disabled={disabled}
                  onChange={(v) => updateRow("vlans", index, "acceptRa", v)}
                  options={ACCEPT_RA_OPTIONS}
                />
              </span>
              <Button
                danger
                size="small"
                disabled={disabled}
                icon={<FontAwesomeIcon icon={faTrash} />}
                aria-label={`Remove VLAN ${index + 1}`}
                onClick={() => removeRow("vlans", index)}
              />
            </div>
          ))}
          <Button
            size="small"
            disabled={disabled}
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => addRow("vlans", newVlan())}
          >
            Add VLAN
          </Button>
        </div>
      ),
    },
    {
      key: "routes",
      label: (
        <span>
          Routes <Tag>{value.routes.length}</Tag>
        </span>
      ),
      children: (
        <div>
          {value.routes.map((route, index) => (
            <div key={`route-${index}`} style={rowStyle}>
              <Input
                style={{ width: "130px" }}
                placeholder="on (mgmt)"
                value={route.on}
                disabled={disabled}
                onChange={(e) => updateRow("routes", index, "on", e.target.value)}
              />
              <Input
                style={{ width: "180px" }}
                placeholder="to (default or CIDR)"
                value={route.to}
                disabled={disabled}
                onChange={(e) => updateRow("routes", index, "to", e.target.value)}
              />
              <Input
                style={{ width: "180px" }}
                placeholder="via"
                value={route.via}
                disabled={disabled}
                onChange={(e) => updateRow("routes", index, "via", e.target.value)}
              />
              <InputNumber
                style={{ width: "110px" }}
                placeholder="table"
                value={route.tableId}
                min={1}
                max={4294967295}
                precision={0}
                aria-label={`Routing table for route ${index + 1}`}
                disabled={disabled}
                onChange={(v) => updateRow("routes", index, "tableId", v)}
              />
              <InputNumber
                style={{ width: "110px" }}
                placeholder="metric"
                value={route.metric}
                min={0}
                max={4294967295}
                precision={0}
                aria-label={`Metric for route ${index + 1}`}
                disabled={disabled}
                onChange={(v) => updateRow("routes", index, "metric", v)}
              />
              <span title="The gateway is reachable directly on this link even though it is outside the interface's own subnet. Without it such a route is rejected as unreachable.">
                On-link{" "}
                <Switch
                  size="small"
                  checked={route.onLink}
                  disabled={disabled}
                  onChange={(c) => updateRow("routes", index, "onLink", c)}
                />
              </span>
              <Button
                danger
                size="small"
                disabled={disabled}
                icon={<FontAwesomeIcon icon={faTrash} />}
                aria-label={`Remove route ${index + 1}`}
                onClick={() => removeRow("routes", index)}
              />
            </div>
          ))}
          <Button
            size="small"
            disabled={disabled}
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => addRow("routes", newRoute())}
          >
            Add route
          </Button>
          <div style={{ marginTop: "8px", opacity: 0.7, fontSize: "12px" }}>
            Leave the table empty for the main routing table. netplan takes a number here, never a
            name like &quot;main&quot;.
          </div>
        </div>
      ),
    },
    {
      key: "rules",
      label: (
        <span>
          Routing policy <Tag>{value.rules.length}</Tag>
        </span>
      ),
      children: (
        <div>
          {value.rules.map((rule, index) => (
            <div key={`rule-${index}`} style={rowStyle}>
              <Input
                style={{ width: "130px" }}
                placeholder="on (mgmt)"
                value={rule.on}
                disabled={disabled}
                onChange={(e) => updateRow("rules", index, "on", e.target.value)}
              />
              <Input
                style={{ width: "200px" }}
                placeholder="from (CIDR)"
                value={rule.from}
                disabled={disabled}
                onChange={(e) => updateRow("rules", index, "from", e.target.value)}
              />
              <Input
                style={{ width: "200px" }}
                placeholder="to (CIDR)"
                value={rule.to}
                disabled={disabled}
                onChange={(e) => updateRow("rules", index, "to", e.target.value)}
              />
              <InputNumber
                style={{ width: "110px" }}
                placeholder="table"
                value={rule.tableId}
                min={1}
                max={4294967295}
                precision={0}
                aria-label={`Routing table for rule ${index + 1}`}
                disabled={disabled}
                onChange={(v) => updateRow("rules", index, "tableId", v)}
              />
              <InputNumber
                style={{ width: "110px" }}
                placeholder="priority"
                value={rule.priority}
                min={0}
                max={4294967295}
                precision={0}
                aria-label={`Priority for rule ${index + 1}`}
                disabled={disabled}
                onChange={(v) => updateRow("rules", index, "priority", v)}
              />
              <InputNumber
                style={{ width: "110px" }}
                placeholder="fwmark"
                min={0}
                max={4294967295}
                precision={0}
                aria-label={`Firewall mark for rule ${index + 1}`}
                value={rule.mark}
                disabled={disabled}
                onChange={(v) => updateRow("rules", index, "mark", v)}
              />
              <Button
                danger
                size="small"
                disabled={disabled}
                icon={<FontAwesomeIcon icon={faTrash} />}
                aria-label={`Remove rule ${index + 1}`}
                onClick={() => removeRow("rules", index)}
              />
            </div>
          ))}
          <Button
            size="small"
            disabled={disabled}
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => addRow("rules", newRule())}
          >
            Add rule
          </Button>
          <div style={{ marginTop: "8px", opacity: 0.7, fontSize: "12px" }}>
            This is what makes a reply leave by the interface it arrived on rather than following
            the main default route.
          </div>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: "16px" }}
        message="Leave this empty to keep the network the gold image was built with"
        description="Anything configured here is applied by the edge server at first boot, after it matches on its MAC address. It is applied behind a reachability probe and rolled back if the box cannot reach the target below, so a mistake here does not strand the machine."
      />

      {problems.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: "16px" }}
          message={`${problems.length} problem${problems.length > 1 ? "s" : ""} to fix before this will work`}
          description={
            <ul style={{ margin: 0, paddingLeft: "18px" }}>
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          }
        />
      )}

      <div style={rowStyle}>
        <Input
          style={{ width: "320px" }}
          addonBefore="Probe target"
          placeholder="10.10.0.1  ·  host:port  ·  [2001:db8::1]:443"
          value={value.probeTarget}
          disabled={disabled}
          onChange={(e) => patch("probeTarget", e.target.value)}
        />
        <Input
          style={{ width: "420px" }}
          addonBefore="Allowed CIDRs"
          placeholder="10.10.0.0/24, 2001:db8:1::/64"
          value={value.allowedCidrs}
          disabled={disabled}
          onChange={(e) => patch("allowedCidrs", e.target.value)}
        />
        <Input
          style={{ width: "320px" }}
          addonBefore="Nameservers"
          placeholder="1.1.1.1, 9.9.9.9"
          aria-label="Nameservers"
          value={value.nameservers}
          disabled={disabled}
          onChange={(e) => patch("nameservers", e.target.value)}
        />
        <Input
          style={{ width: "320px" }}
          addonBefore="Search domains"
          placeholder="site.example"
          aria-label="Search domains"
          value={value.searchDomains}
          disabled={disabled}
          onChange={(e) => patch("searchDomains", e.target.value)}
        />
      </div>

      <Collapse defaultActiveKey={["nics"]}>
        {sections.map((section) => (
          <Collapse.Panel key={section.key} header={section.label}>
            {section.children}
          </Collapse.Panel>
        ))}
      </Collapse>
    </div>
  );
}

export default ServerNetworkEditor;
